import { Button } from '../../../typography/button/button';
import { FormProps } from '../types';
import { SliderInput } from './slider-input';
import { Results } from './results';
import { NewSlider } from './slider';
import { Field } from '@/isomorphic/components/forms/components/field';
import { validators } from '@/hooks/use-validation';
import { useLocalization } from '@/hooks/use-localization';
import { useContext, useMemo } from 'react';
import { AppContext } from '@/contexts/app-context';
import { Arrow } from './svg';
import { ConsumerApplicationProduct, useDataLayer } from '@/hooks/use-data-layer';

export const Form = ({
    calculatorState,
    loanAmountMin,
    loanAmountMax,
    tenorYearsMin,
    tenorYearsMax,
    monthlyAmount,
    formatNumber,
    updateState,
    getLabel,
    login,
    step,
    fields,
    updateFields,
    setStep,
    brandColor,
    partner = 'Carla',
}: FormProps) => {
    const { user } = useContext(AppContext);
    const { getLanguage } = useLocalization();
    const { dataLayerPush } = useDataLayer();
    const validateSsn = (value: string) => validators.socialSecurityNumber(value, getLanguage());
    const govIdValid = useMemo(() => validateSsn(fields.governmentId), [fields.governmentId]);

    const update = (value: number | string | null, key: 'loanAmount' | 'tenorYears' = 'loanAmount') => {
        dataLayerPush({
            event: 'LoanCalculator',
            ProductVariant: partner || 'Carla',
            Product: ConsumerApplicationProduct['BRANDED_CONSUMER_LOAN'],
            LoanValue: key === 'loanAmount' ? value || '' : calculatorState.loanAmount,
            PaybackPeriod: key === 'tenorYears' ? value || '' : calculatorState.tenorYears,
        });

        updateState(key, Number(value));
    };

    return (
        <div className="r-flex r-flex-col r-items-center r-justify-center r-gap-6 md:r-flex-row md:r-gap-10">
            <div className="r-hidden xl:r-block xl:r-w-44" />
            <div className="r-relative r-w-full r-space-y-8 r-pb-6 md:r-max-w-xl md:r-px-4">
                <div className="r-flex r-flex-col">
                    <SliderInput
                        label={getLabel('loanAmountTitle')}
                        suffix={getLabel('currency')}
                        min={loanAmountMin}
                        max={loanAmountMax}
                        value={calculatorState.loanAmount}
                        onChange={value => update(value)}
                    />
                    <NewSlider
                        label={getLabel('loanAmountTitle')}
                        step={step}
                        min={loanAmountMin}
                        max={loanAmountMax}
                        value={calculatorState.loanAmount}
                        brandColor={brandColor}
                        onChange={value => update(value)}
                    />
                    <div className="r-my-6 r-flex r-w-full r-justify-between">
                        <span>
                            {formatNumber(loanAmountMin)} {getLabel('currency')}
                        </span>
                        <span>
                            {formatNumber(loanAmountMax)} {getLabel('currency')}
                        </span>
                    </div>
                </div>
                <div className="r-flex r-flex-col">
                    <SliderInput
                        label={getLabel('tenorTitle')}
                        suffix={
                            typeof calculatorState.tenorYears === 'number' && calculatorState.tenorYears > 1 ? getLabel('years') : getLabel('year')
                        }
                        min={tenorYearsMin}
                        max={tenorYearsMax}
                        value={calculatorState.tenorYears}
                        onChange={value => update(value, 'tenorYears')}
                    />
                    <NewSlider
                        label={getLabel('tenorTitle')}
                        min={tenorYearsMin}
                        max={tenorYearsMax}
                        value={calculatorState.tenorYears}
                        brandColor={brandColor}
                        onChange={value => update(value, 'tenorYears')}
                    />
                    <div className="r-mt-6 r-flex r-w-full r-justify-between">
                        <span>
                            {tenorYearsMin + ' '}
                            {typeof calculatorState.tenorYears === 'number' && calculatorState.tenorYears > 1 ? getLabel('years') : getLabel('year')}
                        </span>
                        <span>
                            {tenorYearsMax + ' '}
                            {typeof calculatorState.tenorYears === 'number' && calculatorState.tenorYears > 1 ? getLabel('years') : getLabel('year')}
                        </span>
                    </div>
                </div>

                <div className="xl:r-hidden">
                    <Results monthlyAmount={monthlyAmount} monthlyAmountTitle={getLabel('monthlyAmountTitle')} brandColor={brandColor} />
                </div>

                {user?.isAuthenticated && fields.governmentId ? (
                    <div className="r-flex r-flex-col">
                        <div className="r-flex r-justify-end">
                            <Button onClick={() => setStep(1)}>
                                <div className="r-flex r-gap-2 r-px-6">
                                    <p>{getLabel('continueButton')}</p>
                                    <Arrow className="r-w-4" />
                                </div>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        {calculatorState.showGovIdField ? (
                            <div>
                                <Field
                                    value={fields.governmentId}
                                    validation={value => validateSsn(value)}
                                    label={getLabel('governmentIdPlaceholder')}
                                    errorMessage={getLabel('governmentIdValidationMessage')}
                                    onChange={value => updateFields({ governmentId: value })}
                                />
                                <p className="r-pt-2 r-text-xs">{getLabel('noCreditCheck')}</p>
                            </div>
                        ) : null}
                        <div className="r-flex r-flex-col">
                            <div className="r-flex r-justify-end [&>button]:r-mr-0">
                                {calculatorState.showGovIdField ? (
                                    <Button
                                        variant="bankidwhite"
                                        brandColor={brandColor}
                                        className="r-px-6"
                                        disabled={!govIdValid}
                                        onClick={() => login()}
                                    >
                                        {getLabel('applyButton')}
                                    </Button>
                                ) : (
                                    <Button
                                        brandColor={brandColor}
                                        className="r-px-6"
                                        onClick={() => updateState('showGovIdField', !calculatorState.showGovIdField)}
                                    >
                                        {getLabel('submitButton')}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className="r-hidden xl:r-flex">
                <Results monthlyAmount={monthlyAmount} monthlyAmountTitle={getLabel('monthlyAmountTitle')} brandColor={brandColor} />
            </div>
        </div>
    );
};
